import { Container } from 'pixi.js';
import {
  DT,
  KITCHEN_TABLE,
  SPORTS_CAR,
  TRACKS,
  collideWalls,
  inFalloff,
  isVisibleAt,
  prepareTrack,
  surfaceAt,
  v,
  type CarSlot,
  type PreparedTrack,
  type RaceConfig,
  type RacePhase,
  type ServerMessage,
  type Snapshot,
  type Vec2,
} from '@nanocars/shared';
import { LOGICAL_HEIGHT, LOGICAL_WIDTH, type Scene, type SceneManager } from './SceneManager';
import { session } from '../state/Session';
import { Prediction } from '../net/Prediction';
import { Interpolation, type RemoteCarTransform } from '../net/Interpolation';
import { Camera } from '../render/Camera';
import { TrackRenderer } from '../render/TrackRenderer';
import { CarRenderer } from '../render/CarRenderer';
import { Effects } from '../render/Effects';
import { Hud, type ChevronWarning, type HudState } from '../render/Hud';
import { colourForSlot } from '../ui/theme';
import type { LiverySlot } from '../assets/manifest';
import { Audio, createMuteButton, MUSIC_DUCK_GAIN, MUSIC_LOBBY_GAIN } from '../audio/Audio';
import { ResultsScene } from './ResultsScene';

const REMOTE_DELAY_MS = 100;
const MAX_ACCUM_S = 0.25;
const OFFSCREEN_WARNING_MARGIN_M = 2;
const WALL_CONTACT_MARGIN_M = 0.08;
const DRIFT_DUST_LATERAL_MPS = 3;
const DRIFT_SKID_LATERAL_MPS = 1.8;
const CHEVRON_SCREEN_MARGIN_PX = 36;
const MUTE_BUTTON_MARGIN = 16;

type ResultsMessage = Extract<ServerMessage, { t: 'results' }>;

// The live race view: runs a fixed 60Hz sim-tick accumulator independent of
// render frame rate (per-tick: poll input, send exactly one `input`
// message, feed Prediction), renders the locally-predicted car every frame
// via Prediction.visual() and every other car via Interpolation.sample(),
// and layers the track, cars, effects and HUD on top. On `results`, hands
// off to ResultsScene.
export class RaceScene implements Scene {
  readonly name = 'race';
  readonly container = new Container();
  private readonly worldContainer = new Container();

  private track!: PreparedTrack;
  private cfg!: RaceConfig;
  private slots: CarSlot[] = [];
  private yourCarId = -1;
  private pxPerM = 1;
  private ready = false;

  private prediction!: Prediction;
  private readonly interp = new Interpolation();
  private camera!: Camera;
  private trackRenderer: TrackRenderer | null = null;
  private readonly carRenderer = new CarRenderer();
  private readonly effects = new Effects();
  private readonly hud = new Hud();

  private accumulator = 0;
  private inputSeq = 0;
  private readonly unsubscribers: Array<() => void> = [];

  private latestSnap: Snapshot | null = null;
  private readonly wasInFalloff = new Map<number, boolean>();

  // Audio-only bookkeeping: which countdown digit was last announced (so
  // beep() fires once per integer, not once per snapshot), the previous
  // snapshot's phase (so go() fires exactly on the countdown->racing edge,
  // matching Hud's own "GO!" trigger), and whether the local car was
  // already touching a wall last frame (so bump() fires once per contact,
  // not every frame the car rests against a wall).
  private lastCountdownInt: number | null = null;
  private prevPhase: RacePhase = 'countdown';
  private wasTouchingWall = false;

  constructor(private readonly scenes: SceneManager) {}

  async enter(): Promise<void> {
    Audio.init();
    void Audio.loadAll();
    // The lobby's music bed keeps playing into the race, just ducked - see
    // LobbyScene.enter()/ResultsScene for the matching un-duck.
    Audio.music.setGain(MUSIC_DUCK_GAIN);

    const start = session.lastRaceStart;
    if (!start) return; // Shouldn't happen via the normal lobby -> raceStart flow.

    this.cfg = start.cfg;
    this.slots = start.slots;
    this.yourCarId = start.yourCarId;
    this.pxPerM = LOGICAL_WIDTH / this.cfg.viewW;

    const trackDef = TRACKS[start.trackId] ?? KITCHEN_TABLE;
    this.track = prepareTrack(trackDef);
    this.prediction = new Prediction(this.track, SPORTS_CAR, this.yourCarId);

    this.worldContainer.scale.set(this.pxPerM);
    this.container.addChild(this.worldContainer);

    const trackRenderer = new TrackRenderer(this.scenes.app, this.track);
    this.trackRenderer = trackRenderer;
    await trackRenderer.build();
    await this.effects.build();

    this.worldContainer.addChild(
      trackRenderer.below,
      this.carRenderer.shadowLayer,
      this.carRenderer.carLayer,
      trackRenderer.above,
      this.effects.layer,
    );

    for (const slot of this.slots) {
      await this.carRenderer.ensureCar(slot.id, slotLivery(slot));
    }

    const centre: Vec2 = {
      x: (this.track.bounds.min.x + this.track.bounds.max.x) / 2,
      y: (this.track.bounds.min.y + this.track.bounds.max.y) / 2,
    };
    this.camera = new Camera(this.worldContainer, this.pxPerM, LOGICAL_WIDTH, LOGICAL_HEIGHT, centre);

    this.container.addChild(this.hud.container);

    const muteButton = createMuteButton();
    // Bottom-right: the top-right corner belongs to the lap counter.
    muteButton.x = LOGICAL_WIDTH - muteButton.width - MUTE_BUTTON_MARGIN;
    muteButton.y = LOGICAL_HEIGHT - muteButton.height - MUTE_BUTTON_MARGIN;
    this.container.addChild(muteButton);

    this.unsubscribers.push(
      session.net.on('snap', (msg) => this.onSnap(msg.s)),
      session.net.on('results', (msg) => this.onResults(msg)),
      // Nothing in the race view draws from LobbyState, but the server does
      // broadcast one on every membership change -- including a host
      // migration mid-race. Keeping Session current here is what lets
      // ResultsScene, entered straight from this scene, know who the host
      // is now rather than who it was when the race started.
      session.net.on('lobby', (msg) => {
        session.lobby = msg.lobby;
      }),
    );

    this.ready = true;
  }

  exit(): void {
    for (const unsub of this.unsubscribers) unsub();
    this.unsubscribers.length = 0;
    Audio.stopAllEngines();

    // Release the GPU resources this scene allocated. A race is entered
    // afresh every rematch, so without this each one leaked a full-track
    // skid RenderTexture, every car's 32 frame views, the HUD subtree and
    // the whole particle layer. Assets-cached textures are shared and are
    // deliberately left alive for the next race.
    this.ready = false;
    this.trackRenderer?.destroy();
    this.trackRenderer = null;
    this.carRenderer.destroy();
    this.effects.destroy();
    this.hud.destroy();
    // Whatever is left directly under the scene container (the world
    // container and the mute button). Pixi's Container.destroy is a no-op on
    // an already-destroyed node, so the layers the renderers above have
    // already torn down are harmless here. The scene container ITSELF is not
    // destroyed: SceneManager.go still removes it from the root after exit().
    for (const child of this.container.removeChildren()) child.destroy({ children: true });
    this.latestSnap = null;
    this.wasInFalloff.clear();
  }

  update(dt: number): void {
    if (!this.ready) return;

    this.accumulator = Math.min(this.accumulator + dt, MAX_ACCUM_S);
    while (this.accumulator >= DT) {
      this.tick();
      this.accumulator -= DT;
    }

    this.render(dt);
  }

  resize(_w: number, _h: number): void {
    // Fixed 1280x720 logical layout.
  }

  private tick(): void {
    const seq = this.inputSeq++;
    const frame = session.input.poll(seq);
    session.net.send({ t: 'input', seq, th: frame.throttle, br: frame.brake, st: frame.steer });
    this.prediction.pushInput(frame);
  }

  private onSnap(s: Snapshot): void {
    this.latestSnap = s;
    const own = s.cars.find((c) => c.id === this.yourCarId);
    if (own) this.prediction.onSnapshot(s, own);
    this.interp.push(s, performance.now());

    for (const car of s.cars) {
      const was = this.wasInFalloff.get(car.id) ?? false;
      const now = inFalloff(this.track, { x: car.x, y: car.y });
      if (!was && now) {
        this.effects.splash({ x: car.x, y: car.y });
        Audio.fall();
      }
      this.wasInFalloff.set(car.id, now);
    }

    this.updateCountdownAudio(s);
    this.prevPhase = s.phase;
  }

  // beep() once per whole second remaining (matching Hud's own
  // countdownLength-phaseTime->"3, 2, 1, GO!" display), go() exactly on the
  // countdown->racing edge (same edge Hud uses to show its "GO!" banner).
  private updateCountdownAudio(s: Snapshot): void {
    if (s.phase === 'countdown') {
      const remaining = s.countdownLength - s.phaseTime;
      const displayInt = remaining > 0.5 ? Math.ceil(remaining) : 0;
      if (displayInt !== this.lastCountdownInt) {
        if (displayInt > 0) Audio.beep();
        this.lastCountdownInt = displayInt;
      }
    } else {
      this.lastCountdownInt = null;
      if (this.prevPhase === 'countdown' && s.phase === 'racing') Audio.go();
    }
  }

  private onResults(msg: ResultsMessage): void {
    Audio.finish();
    Audio.music.setGain(MUSIC_LOBBY_GAIN);
    void this.scenes.go(new ResultsScene(this.scenes, msg));
  }

  private render(dt: number): void {
    const now = performance.now();
    const visual = this.prediction.visual();

    if (this.carRenderer.hasCar(this.yourCarId)) {
      this.carRenderer.update(this.yourCarId, {
        pos: visual.pos,
        heading: visual.heading,
        air: this.prediction.car.airborne,
        eliminated: this.prediction.car.eliminated,
      });
    }

    const others = this.interp.sample(now, REMOTE_DELAY_MS);
    for (const [id, t] of others) {
      if (id === this.yourCarId) continue;
      if (!this.carRenderer.hasCar(id)) continue;
      this.carRenderer.update(id, t);
    }

    const serverCam = this.latestSnap?.cam ?? visual.pos;
    this.camera.update(serverCam, dt);

    this.effects.update(dt);
    this.applyOwnEffects(visual);
    this.updateEngines(visual, others);

    this.hud.update(this.buildHudState(others), dt);
  }

  // Dust puffs, skid marks and the skid/bump one-shots are driven off the
  // LOCAL predicted car's real physics (vel, surface, wall proximity), which
  // is only available for the player's own car - remote cars only carry the
  // interpolated pos/heading the wire protocol sends, not velocity.
  private applyOwnEffects(visual: { pos: Vec2; heading: number }): void {
    const car = this.prediction.car;
    if (car.eliminated || car.airborne > 0) {
      this.wasTouchingWall = false;
      return;
    }

    const fwd = v.fromAngle(car.heading);
    const right = { x: -fwd.y, y: fwd.x };
    const lateralSpeed = Math.abs(v.dot(car.vel, right));
    const touchingWall = collideWalls(this.track, car.pos, SPORTS_CAR.radius + WALL_CONTACT_MARGIN_M) !== null;

    if (lateralSpeed > DRIFT_DUST_LATERAL_MPS || touchingWall) {
      this.effects.maybeDust(this.yourCarId, visual.pos, car.heading);
    }
    // Same 3 m/s lateral-speed threshold as the dust puff above.
    if (lateralSpeed > DRIFT_DUST_LATERAL_MPS) Audio.skid(this.yourCarId);

    // Edge-triggered (not "while touching") so resting against a wall plays
    // one bump on contact, not a bump every single frame.
    if (touchingWall && !this.wasTouchingWall) Audio.bump();
    this.wasTouchingWall = touchingWall;

    const surface = surfaceAt(this.track, car.pos);
    if (surface.name === 'table' && lateralSpeed > DRIFT_SKID_LATERAL_MPS) {
      this.trackRenderer?.paintSkid(car.pos, car.heading, Math.min(0.5, lateralSpeed / 12));
    }
  }

  // Runs the looping engine sound for every currently-heard car (own car
  // from Prediction's real velocity; other cars from the latest raw
  // snapshot's vx/vy, since RemoteCarTransform only carries the interpolated
  // pos/heading, not velocity) and stops any voice for a car that's no
  // longer eliminated-free/visible this frame.
  private updateEngines(visual: { pos: Vec2; heading: number }, others: ReadonlyMap<number, RemoteCarTransform>): void {
    const camPos = this.camera.worldPos;
    const activeIds = new Set<number>();
    const car = this.prediction.car;

    if (!car.eliminated) {
      const speedFrac = Math.min(1, v.len(car.vel) / SPORTS_CAR.maxSpeed);
      const dist = v.len(v.sub(visual.pos, camPos));
      Audio.engine(this.yourCarId, speedFrac, dist);
      activeIds.add(this.yourCarId);
    }

    for (const [id, t] of others) {
      if (id === this.yourCarId || t.el) continue;
      const raw = this.latestSnap?.cars.find((c) => c.id === id);
      const speed = raw ? Math.hypot(raw.vx, raw.vy) : 0;
      const speedFrac = Math.min(1, speed / SPORTS_CAR.maxSpeed);
      const dist = v.len(v.sub(t.pos, camPos));
      Audio.engine(id, speedFrac, dist);
      activeIds.add(id);
    }

    Audio.stopStaleEngines(activeIds);
  }

  private buildHudState(others: ReadonlyMap<number, RemoteCarTransform>): HudState {
    void others; // reserved for a future "ghost" HUD indicator; not needed yet
    const s = this.latestSnap;
    const ownSnap = s?.cars.find((c) => c.id === this.yourCarId);

    return {
      slots: this.slots,
      scores: s?.scores ?? {},
      phase: s?.phase ?? 'countdown',
      phaseTime: s?.phaseTime ?? 0,
      countdownLength: s?.countdownLength ?? this.cfg.countdown,
      roundWinner: s?.roundWinner ?? null,
      ownLap: ownSnap?.lap ?? 0,
      lapLimit: this.cfg.lapLimit,
      wrongWay: ownSnap?.ww === 1,
      chevrons: s ? this.buildChevrons(s) : [],
    };
  }

  // Off-screen warnings use the SERVER's camera position (the authority on
  // who is about to be eliminated), not the eased render camera, so what
  // the player sees matches what the server is actually about to judge.
  private buildChevrons(s: Snapshot): ChevronWarning[] {
    const halfW = this.cfg.viewW / 2;
    const halfH = this.cfg.viewH / 2;
    const chevrons: ChevronWarning[] = [];

    for (const car of s.cars) {
      if (car.el === 1) continue;

      const dx = car.x - s.cam.x;
      const dy = car.y - s.cam.y;
      const marginX = halfW - Math.abs(dx);
      const marginY = halfH - Math.abs(dy);
      const margin = Math.min(marginX, marginY);
      if (margin > OFFSCREEN_WARNING_MARGIN_M) continue;

      const slot = this.slots.find((sl) => sl.id === car.id);
      const colour = colourForSlot(slot?.colour ?? 0);
      const edge = clampToScreenEdge(dx * this.pxPerM, dy * this.pxPerM, LOGICAL_WIDTH, LOGICAL_HEIGHT, CHEVRON_SCREEN_MARGIN_PX);

      chevrons.push({
        colour,
        screenPos: { x: LOGICAL_WIDTH / 2 + edge.x, y: LOGICAL_HEIGHT / 2 + edge.y },
        angle: Math.atan2(edge.y, edge.x),
        // Same rule updateElimination uses server-side to start the
        // offscreen-grace timer, via the shared isVisibleAt helper (see
        // race.ts) rather than re-deriving it from marginX/marginY here.
        flashing: !isVisibleAt(s.cam, this.cfg, { x: car.x, y: car.y }),
      });
    }

    return chevrons;
  }
}

// LobbyPlayer/CarSlot.colour is a slot INDEX (0..5), the same index space as
// the six liveried sprite sheets (see assets/manifest.ts LIVERY_SLOTS and
// ui/theme.ts PLAYER_COLORS - both documented as "same order").
function slotLivery(slot: CarSlot): LiverySlot {
  const idx = slot.colour;
  return (idx >= 0 && idx <= 5 ? idx : 0) as LiverySlot;
}

// Projects a direction vector from screen centre out to the nearest edge of
// a centred rectangle inset by `margin` on every side.
function clampToScreenEdge(dx: number, dy: number, screenW: number, screenH: number, margin: number): Vec2 {
  const halfW = screenW / 2 - margin;
  const halfH = screenH / 2 - margin;
  if (dx === 0 && dy === 0) return { x: halfW, y: 0 };
  const scaleX = dx !== 0 ? halfW / Math.abs(dx) : Infinity;
  const scaleY = dy !== 0 ? halfH / Math.abs(dy) : Infinity;
  const scale = Math.min(scaleX, scaleY);
  return { x: dx * scale, y: dy * scale };
}
